import React, { useState, useRef } from 'react';
import { Panel, PanelSection } from '../ui/Panel';
import { Button } from '../ui/Button';

// Preset colors for the color picker
const LAYER_COLORS = [
  '#ffffff', '#ff0000', '#00ff00', '#0000ff', '#ffff00',
  '#ff00ff', '#00ffff', '#ff8800', '#00c8ff', '#00ffaa',
  '#ff6666', '#66ff66', '#6666ff', '#ffaa00', '#aa00ff',
  '#888888', '#aaaaaa', '#446688', '#886644', '#668844',
];

// HVAC layer definitions with colors and labels
const HVAC_LAYER_DEFS = [
  { id: 'supplyDucts', label: 'Supply Ducts', color: '#e65100' },
  { id: 'returnDucts', label: 'Return Ducts', color: '#1565c0' },
  { id: 'supplyVents', label: 'Supply Vents', color: '#ff7043' },
  { id: 'returnGrilles', label: 'Return Grilles', color: '#42a5f5' },
  { id: 'equipment', label: 'Equipment', color: '#66bb6a' },
  { id: 'fittings', label: 'Fittings', color: '#ab47bc' },
];

/**
 * LayersPanel - Layer visibility and lock controls
 */
export const LayersPanel = ({
  layers,
  onToggleVisibility,
  onToggleLock,
  onSetColor,
  onShowAll,
  onHideAll,
  onClose,
  isMobile = false,
  // HVAC layer props
  hvacMode = false,
  hvacLayers = null,
  onUpdateHvacLayer = null,
}) => {
  const [colorPickerLayer, setColorPickerLayer] = useState(null);
  const colorInputRef = useRef(null);
  return (
    <Panel
      title="Layers"
      onClose={onClose}
      position="right"
      width={isMobile ? 280 : 240}
    >
      {/* Quick actions */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
        <Button
          variant="default"
          onClick={onShowAll}
          size="small"
          style={{ flex: 1 }}
        >
          Show All
        </Button>
        <Button
          variant="default"
          onClick={onHideAll}
          size="small"
          style={{ flex: 1 }}
        >
          Hide All
        </Button>
      </div>

      {/* Layer list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {Object.entries(layers).map(([id, layer]) => (
          <div
            key={id}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '8px 10px',
              background: 'rgba(255,255,255,0.04)',
              borderRadius: '6px',
              gap: '8px',
            }}
          >
            {/* Visibility toggle */}
            <button
              onClick={() => onToggleVisibility(id)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                padding: '2px',
                opacity: layer.visible ? 1 : 0.4,
              }}
              title={layer.visible ? 'Hide layer' : 'Show layer'}
            >
              {layer.visible ? '👁️' : '👁️‍🗨️'}
            </button>

            {/* Lock toggle */}
            <button
              onClick={() => onToggleLock(id)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '12px',
                padding: '2px',
                opacity: layer.locked ? 1 : 0.4,
              }}
              title={layer.locked ? 'Unlock layer' : 'Lock layer'}
            >
              {layer.locked ? '🔒' : '🔓'}
            </button>

            {/* Layer color indicator - clickable */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setColorPickerLayer(colorPickerLayer === id ? null : id)}
                style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '3px',
                  background: layer.color,
                  border: '2px solid rgba(255,255,255,0.3)',
                  cursor: 'pointer',
                  padding: 0,
                }}
                title="Change layer color"
              />
              {/* Color picker dropdown */}
              {colorPickerLayer === id && (
                <div
                  style={{
                    position: 'absolute',
                    top: '24px',
                    left: '0',
                    zIndex: 1000,
                    background: 'rgba(20,28,36,0.98)',
                    border: '1px solid rgba(0,200,255,0.3)',
                    borderRadius: '8px',
                    padding: '8px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
                    width: '140px',
                  }}
                >
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(5, 1fr)',
                    gap: '4px',
                    marginBottom: '8px',
                  }}>
                    {LAYER_COLORS.map(color => (
                      <button
                        key={color}
                        onClick={() => {
                          onSetColor?.(id, color);
                          setColorPickerLayer(null);
                        }}
                        style={{
                          width: '22px',
                          height: '22px',
                          borderRadius: '3px',
                          background: color,
                          border: layer.color === color ? '2px solid #00ffaa' : '1px solid rgba(255,255,255,0.2)',
                          cursor: 'pointer',
                          padding: 0,
                        }}
                      />
                    ))}
                  </div>
                  {/* Custom color input */}
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <input
                      ref={colorInputRef}
                      type="color"
                      value={layer.color}
                      onChange={(e) => {
                        onSetColor?.(id, e.target.value);
                      }}
                      style={{
                        width: '28px',
                        height: '22px',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        padding: 0,
                      }}
                    />
                    <span style={{ fontSize: '10px', color: '#6080a0' }}>Custom</span>
                  </div>
                </div>
              )}
            </div>

            {/* Layer name */}
            <span
              style={{
                flex: 1,
                color: layer.visible ? '#d0d8e0' : '#6080a0',
                fontSize: '12px',
                textDecoration: layer.locked ? 'none' : 'none',
              }}
            >
              {layer.name}
            </span>
          </div>
        ))}
      </div>

      {/* HVAC Layers Section - only show when HVAC mode is active */}
      {hvacMode && hvacLayers && (
        <>
          <div style={{
            marginTop: '16px',
            paddingTop: '12px',
            borderTop: '1px solid rgba(255,255,255,0.1)',
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px',
            }}>
              <span style={{ fontSize: '11px', color: '#00c8ff', fontWeight: '600', textTransform: 'uppercase' }}>
                HVAC Layers
              </span>
              <div style={{ display: 'flex', gap: '4px' }}>
                <button
                  onClick={() => {
                    HVAC_LAYER_DEFS.forEach(def => {
                      onUpdateHvacLayer?.(def.id, { ...hvacLayers[def.id], visible: true });
                    });
                  }}
                  style={{
                    background: 'rgba(255,255,255,0.1)',
                    border: 'none',
                    borderRadius: '3px',
                    color: '#8090a0',
                    fontSize: '10px',
                    padding: '2px 6px',
                    cursor: 'pointer',
                  }}
                >
                  All
                </button>
                <button
                  onClick={() => {
                    HVAC_LAYER_DEFS.forEach(def => {
                      onUpdateHvacLayer?.(def.id, { ...hvacLayers[def.id], visible: false });
                    });
                  }}
                  style={{
                    background: 'rgba(255,255,255,0.1)',
                    border: 'none',
                    borderRadius: '3px',
                    color: '#8090a0',
                    fontSize: '10px',
                    padding: '2px 6px',
                    cursor: 'pointer',
                  }}
                >
                  None
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {HVAC_LAYER_DEFS.map(def => {
                const layerState = hvacLayers[def.id] || { visible: true, locked: false, opacity: 1 };
                return (
                  <div
                    key={def.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '6px 10px',
                      background: 'rgba(255,255,255,0.04)',
                      borderRadius: '6px',
                      gap: '8px',
                    }}
                  >
                    {/* Visibility toggle */}
                    <button
                      onClick={() => onUpdateHvacLayer?.(def.id, { ...layerState, visible: !layerState.visible })}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '14px',
                        padding: '2px',
                        opacity: layerState.visible ? 1 : 0.4,
                      }}
                      title={layerState.visible ? 'Hide layer' : 'Show layer'}
                    >
                      {layerState.visible ? '👁️' : '👁️‍🗨️'}
                    </button>

                    {/* Lock toggle */}
                    <button
                      onClick={() => onUpdateHvacLayer?.(def.id, { ...layerState, locked: !layerState.locked })}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '12px',
                        padding: '2px',
                        opacity: layerState.locked ? 1 : 0.4,
                      }}
                      title={layerState.locked ? 'Unlock layer' : 'Lock layer'}
                    >
                      {layerState.locked ? '🔒' : '🔓'}
                    </button>

                    {/* Color indicator */}
                    <div
                      style={{
                        width: '14px',
                        height: '14px',
                        borderRadius: '3px',
                        background: def.color,
                        border: '1px solid rgba(255,255,255,0.3)',
                        opacity: layerState.visible ? layerState.opacity : 0.3,
                      }}
                    />

                    {/* Layer name */}
                    <span
                      style={{
                        flex: 1,
                        color: layerState.visible ? '#d0d8e0' : '#6080a0',
                        fontSize: '11px',
                      }}
                    >
                      {def.label}
                    </span>

                    {/* Opacity slider */}
                    <input
                      type="range"
                      min="0.1"
                      max="1"
                      step="0.1"
                      value={layerState.opacity}
                      onChange={(e) => onUpdateHvacLayer?.(def.id, { ...layerState, opacity: parseFloat(e.target.value) })}
                      style={{
                        width: '40px',
                        height: '4px',
                        cursor: 'pointer',
                        accentColor: def.color,
                      }}
                      title={`Opacity: ${Math.round(layerState.opacity * 100)}%`}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* HVAC Legend */}
          <div style={{
            marginTop: '12px',
            padding: '8px 10px',
            background: 'rgba(0,0,0,0.2)',
            borderRadius: '6px',
            fontSize: '10px',
            color: '#6080a0',
          }}>
            <div style={{ marginBottom: '4px', fontWeight: '600' }}>Legend</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
              <span style={{ width: '10px', height: '10px', borderRadius: '2px', background: '#e65100' }} />
              <span>Supply (heating/cooling)</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ width: '10px', height: '10px', borderRadius: '2px', background: '#1565c0' }} />
              <span>Return (air return)</span>
            </div>
          </div>
        </>
      )}
    </Panel>
  );
};

export default LayersPanel;
